import React, { useState, useEffect } from 'react';
import { User, Mail, Phone, MapPin, Edit3, Camera, Save, X, Loader } from 'lucide-react';
import Layout from '../Layout';
import ParentAPI from '../services/ParentAPI';
import { showErrorNotification, showSuccessNotification } from '../utils/errorHandler';
import BeneficiaryFeedbackCard from '../Components/BeneficiaryFeedbackCard';

const ParentProfile = () => {
    const [parentInfo, setParentInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: ''
    });

    useEffect(() => {
        loadParentProfile();
    }, []);

    const loadParentProfile = async () => {
        try {
            setLoading(true);
            const userInfo = await ParentAPI.getCurrentUser();
            setParentInfo(userInfo);
            setFormData({
                name: userInfo.name || '',
                email: userInfo.email || '',
                phone: userInfo.phone || '',
                address: userInfo.address || ''
            });
        } catch (err) {
            console.error('Error loading parent profile:', err);
            showErrorNotification('Failed to load profile information');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSave = async () => {
        try {
            // This would need to be implemented in the backend
            // For now, we'll just update the local state
            setParentInfo(prev => ({
                ...prev,
                ...formData
            }));
            setEditing(false);
            showSuccessNotification('Profile updated successfully');
        } catch (err) {
            console.error('Error updating profile:', err);
            showErrorNotification('Failed to update profile');
        }
    };

    const handleCancel = () => {
        setFormData({
            name: parentInfo.name || '',
            email: parentInfo.email || '',
            phone: parentInfo.phone || '',
            address: parentInfo.address || ''
        });
        setEditing(false);
    };

    if (loading) {
        return (
            <Layout pageTitle="Parent's Profile">
                <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center">
                    <div className="text-center">
                        <Loader className="w-12 h-12 animate-spin mx-auto mb-4 text-green-600" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Loading Profile</h2>
                        <p className="text-gray-600">Please wait while we fetch your profile information...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!parentInfo) {
        return (
            <Layout pageTitle="Parent's Profile">
                <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center">
                    <div className="text-center">
                        <User className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Profile Not Available</h2>
                        <p className="text-gray-600 mb-4">Unable to load profile information.</p>
                        <button 
                            onClick={loadParentProfile}
                            className="bg-green-500 hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout pageTitle="Parent's Profile">
            <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 p-6">
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="bg-white rounded-2xl shadow-xl p-6 mb-8">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-3xl font-bold text-gray-800">Parent's Profile</h1>
                                <p className="text-gray-600">Manage your personal information and preferences</p>
                            </div>
                            
                            {!editing ? (
                                <button
                                    onClick={() => setEditing(true)}
                                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                                >
                                    <Edit3 size={16} />
                                    Edit Profile
                                </button>
                            ) : (
                                <div className="flex gap-2">
                                    <button
                                        onClick={handleSave}
                                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                                    >
                                        <Save size={16} />
                                        Save
                                    </button>
                                    <button
                                        onClick={handleCancel}
                                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                                    >
                                        <X size={16} />
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Profile Picture and Basic Info */}
                        <div className="lg:col-span-1">
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <div className="text-center">
                                    <div className="relative inline-block mb-6">
                                        <div className="w-32 h-32 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center text-white text-4xl font-bold shadow-lg">
                                            {parentInfo.profile_picture_url ? (
                                                <img 
                                                    src={parentInfo.profile_picture_url} 
                                                    alt={parentInfo.name}
                                                    className="w-32 h-32 rounded-full object-cover"
                                                />
                                            ) : (
                                                parentInfo.name?.charAt(0).toUpperCase() || 'P'
                                            )}
                                        </div>
                                        <button className="absolute bottom-0 right-0 bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full shadow-lg transition-colors">
                                            <Camera size={16} />
                                        </button>
                                    </div>
                                    
                                    <h2 className="text-2xl font-bold text-gray-800 mb-2">{parentInfo.name}</h2>
                                    <p className="text-gray-600 mb-4">Parent Account</p>
                                    
                                    <div className="bg-green-50 rounded-lg p-4">
                                        <div className="flex items-center justify-center text-green-600 mb-2">
                                            <User className="w-5 h-5 mr-2" />
                                            <span className="font-medium">Account Status</span>
                                        </div>
                                        <p className="text-sm text-gray-700">Active Parent Account</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Detailed Information */}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-2xl shadow-xl p-6">
                                <h3 className="text-2xl font-bold text-gray-800 mb-6">Personal Information</h3>

                                <div className="space-y-6">
                                    {/* Name */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Full Name
                                        </label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Enter your full name"
                                            />
                                        ) : (
                                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                                <User className="w-5 h-5 text-blue-500" />
                                                <span className="font-medium text-gray-800">{parentInfo.name}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Email */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Email Address
                                        </label>
                                        {editing ? (
                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Enter your email address"
                                            />
                                        ) : (
                                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                                <Mail className="w-5 h-5 text-green-500" />
                                                <span className="font-medium text-gray-800">{parentInfo.email}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Phone */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Phone Number
                                        </label>
                                        {editing ? (
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Enter your phone number"
                                            />
                                        ) : (
                                            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                                                <Phone className="w-5 h-5 text-purple-500" />
                                                <span className="font-medium text-gray-800">
                                                    {formData.phone || 'Not provided'}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Address */}
                                    <div>
                                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                                            Address
                                        </label>
                                        {editing ? (
                                            <textarea
                                                name="address"
                                                value={formData.address}
                                                onChange={handleInputChange}
                                                rows={3}
                                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Enter your address"
                                            />
                                        ) : (
                                            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                                <MapPin className="w-5 h-5 text-orange-500 mt-0.5" />
                                                <span className="font-medium text-gray-800">
                                                    {formData.address || 'Not provided'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Account Information */}
                                <div className="mt-8 pt-6 border-t border-gray-200">
                                    <h4 className="text-lg font-semibold text-gray-700 mb-4">Account Information</h4>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="bg-blue-50 rounded-lg p-4">
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-blue-600">Parent</div>
                                                <div className="text-sm text-gray-600">Account Role</div>
                                            </div>
                                        </div>
                                        
                                        <div className="bg-green-50 rounded-lg p-4">
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-green-600">Active</div>
                                                <div className="text-sm text-gray-600">Account Status</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Additional Settings */}
                    <div className="mt-8 bg-white rounded-2xl shadow-xl p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-4">Preferences & Settings</h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <h4 className="font-semibold text-gray-700">Notification Preferences</h4>
                                <div className="space-y-2">
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded text-blue-600" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-600">Email notifications for attendance</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded text-blue-600" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-600">Assessment result updates</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded text-blue-600" />
                                        <span className="ml-2 text-sm text-gray-600">Weekly progress reports</span>
                                    </label>
                                </div>
                            </div>
                            
                            <div className="space-y-4">
                                <h4 className="font-semibold text-gray-700">Privacy Settings</h4>
                                <div className="space-y-2">
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded text-blue-600" defaultChecked />
                                        <span className="ml-2 text-sm text-gray-600">Share progress with teachers</span>
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" className="rounded text-blue-600" />
                                        <span className="ml-2 text-sm text-gray-600">Allow data analytics</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="mt-8">
                        <BeneficiaryFeedbackCard />
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default ParentProfile;
